import { Injectable } from '@angular/core';
import { ChartTypes } from '@mode/shared/contract-common';
import {
  ChartDomain,
  DataViewResponseTypes,
  SwitchViewDomain,
  SwitchViewResponseTypes,
} from '@mode/shared/contract-common';
import { ApiService, getEmbeddedCollection } from '@mode/shared/util-halogen';
import { Observable, from, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { toChart } from '../data-mappers/chart-data-mappers';
import { normalizeSwitchView } from '../data-mappers/data-view-data-mappers';
import { switchViewResourceHasDataset, toSwitchView } from '../data-mappers/switch-view-data-mappers';
import { ReportClientService } from '../reports/report-client.service';
import { chartCache, reportCache, switchViewCache } from '../resource-cache';
import { ChartResource } from './chart-response.types';

@Injectable({
  providedIn: 'root',
})
export class ChartClientService {
  constructor(
    private apiService: ApiService,
    private reportClient: ReportClientService
  ) {}

  /**
   * Load single chart.
   */
  public getChart(accountId: string, chartToken: string): Observable<ChartDomain.Chart> {
    const url = `/api/${accountId}/visualizations/${chartToken}`;
    const cached = chartCache.get(chartToken);

    return from(cached != null ? of(cached) : this.apiService.getFromPath<ChartResource>(url)).pipe(
      tap((resource) => chartCache.set(resource.token, resource)),
      map((resource) => toChart(resource))
    );
  }

  /**
   * Load single chart with switch view.
   * Question: Do we need a container type for the chart + switch view?
   */
  public getChartWithSwitchView(
    accountId: string,
    chartToken: string
  ): Observable<{ chart: ChartDomain.Chart; switchView: SwitchViewDomain.SwitchView }> {
    const url = `/api/${accountId}/visualizations/${chartToken}`;
    const cached = chartCache.get(chartToken);

    return from(
      cached != null
        ? of(cached)
        : this.apiService.getFromPath<ChartResource>(url, {
            includes: {
              switch_view: 'switch_attrs',
            },
          })
    ).pipe(
      tap((resource) => chartCache.set(resource.token, resource)),
      switchMap((resource) => {
        const switchView = switchViewCache.get(resource.switch_view_token);
        if (switchView != null) {
          return of(switchView).pipe(
            map((switchViewResource) => ({
              chart: toChart(resource),
              switchView: toSwitchView(
                switchViewResource,
                getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewAttrResource>(
                  switchViewResource,
                  'switch_attrs'
                )
              ),
            }))
          );
        } else {
          return from(
            this.apiService.getResource<SwitchViewResponseTypes.SwitchViewResource>(resource, 'switch_view')
          ).pipe(
            tap((switchViewResource) => switchViewCache.set(switchViewResource.token, switchViewResource)),
            map((switchViewResource) => ({
              chart: toChart(resource),
              switchView: toSwitchView(
                switchViewResource,
                getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewAttrResource>(
                  switchViewResource,
                  'switch_attrs'
                )
              ),
            }))
          );
        }
      })
    );
  }

  public getChartsFromViewsOnReport(accountId: string, reportToken: string) {
    return this.reportClient.getReportWithViews(accountId, reportToken).pipe(
      map(() => reportCache.get(reportToken)),
      map((reportResource) => {
        if (reportResource == null) {
          return [];
        }

        const importedSwitchViews =
          getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewResource>(
            reportResource,
            'imported_switch_views',
            'switch_views'
          ) || [];

        // Visualizations cannot exist on switchViews based on unrun Dataset Creators, so we filter out the unrun switchViews here to prevent errors
        const localSwitchViews =
          getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewResource>(
            reportResource,
            'local_switch_views',
            'switch_views'
          ) || [];
        const localSwitchViewsWithRuns = localSwitchViews.filter((switchViewResource) =>
          switchViewResourceHasDataset(switchViewResource)
        );

        return importedSwitchViews.concat(localSwitchViewsWithRuns);
      }),
      tap((switchViewResources) => {
        switchViewResources.forEach((view) => {
          const chartResources = getEmbeddedCollection<ChartResource>(view, 'visualizations') || [];
          chartResources
            ?.filter((chartResource) => chartResource.view_version === 2) // Legacy viz do not come in through data-access, they use legacy query based flow
            .forEach((chart) => {
              chartCache.set(chart.token, chart);
            });
        });
      }),
      map((switchViewResources) => {
        const allCharts: ChartDomain.ChartWithQueryToken[] = [];

        switchViewResources.forEach((view) => {
          const chartResources = getEmbeddedCollection<ChartResource>(view, 'visualizations') || [];

          // TODO - temporary to get the query token, still need to followup to refactor dataset result
          //   types to extend HAL resource pattern to use API functions.
          const switchViewResponse = view as any as DataViewResponseTypes.SwitchViewWithRunsResponses;
          const normalizedSwitchView = normalizeSwitchView(switchViewResponse);
          const queryToken = normalizedSwitchView._embedded.dataset.query_token;

          const charts: ChartDomain.ChartWithQueryToken[] = chartResources
            .filter((chartResource) => chartResource.view_version === 2)
            .map((chartResource) => {
              const mappedChart = toChart(chartResource);
              return {
                ...mappedChart,
                queryToken,
              };
            });

          allCharts.push(...charts);
        });

        return allCharts;
      })
    );
  }

  public getCharts(switchViewToken: string) {
    const view = switchViewCache.get(switchViewToken);

    if (view != null) {
      return from(this.apiService.getResources<ChartResource>(view, 'visualizations')).pipe(
        map((resources) => resources.map((resource) => toChart(resource)))
      );
    } else {
      return throwError(new Error(`switch view ${switchViewToken} is not in the cache`));
    }
  }

  public createChart(newChart: ChartDomain.NewChart) {
    const view = switchViewCache.get(newChart.switchViewToken);
    if (view != null) {
      const visualization = {
        color_palette_token: newChart.colorPaletteToken,
        view_version: 2, // this can only create V2 charts
        view_vegas: {
          encoding: newChart.encoding,
          format: newChart.format,
          chartType: newChart.chartType,
          title: '',
          description: '',
          caption: ChartTypes.EMPTY_CAPTION,
          fieldFormats: newChart.fieldFormats ?? {},
          fieldColors: newChart.fieldColors ?? {},
          facetSizes: {},
          isAutoUpdateDisabled: !!newChart.isAutoUpdateDisabled,
        },
        view: {}, // for V1 charts, leaving blank
      };

      const params = {
        visualization,
      };

      return from(this.apiService.create<ChartResource>(view, 'new_visualization', params)).pipe(
        tap((response) => chartCache.set(response.token, response)),
        map((response) => toChart(response))
      );
    } else {
      return throwError(new Error(`switch view ${newChart.switchViewToken} is not in the cache`));
    }
  }

  /**
   * Used to clone an existing chart.
   *
   * Defined separately from `createChart` for now so we can be explicit about the expectation
   * around the data we get passed. We could abstract this more if we have more entrypoints
   * in the future around the shared api call.
   */
  public cloneChart(clonedChart: ChartDomain.ClonedChart, prefixCopyTitle = true) {
    const view = switchViewCache.get(clonedChart.switchViewToken);
    if (view != null) {
      const visualization = {
        color_palette_token: clonedChart.colorPaletteToken,
        view_version: 2, // this can only create V2 charts
        view_vegas: {
          encoding: clonedChart.encoding,
          format: { ...clonedChart.format, useMuzeRenderer: clonedChart.format.useMuzeRenderer ?? false },
          chartType: clonedChart.chartType,
          title: prefixCopyTitle && clonedChart.title ? 'Copy of ' + clonedChart.title : (clonedChart.title ?? ''),
          caption: clonedChart.caption,
          description: clonedChart.description,
          fieldFormats: clonedChart.fieldFormats,
          fieldColors: clonedChart.fieldColors,
          facetSizes: clonedChart.facetSizes,
          isAutoUpdateDisabled: clonedChart.isAutoUpdateDisabled,
        },
        view: {}, // for V1 charts only, leaving blank
      };

      const params = {
        visualization,
      };

      return from(this.apiService.create<ChartResource>(view, 'new_visualization', params)).pipe(
        tap((response) => chartCache.set(response.token, response)),
        map((response) => toChart(response))
      );
    } else {
      return throwError(new Error(`switch view ${clonedChart.switchViewToken} is not in the cache`));
    }
  }

  public updateChart(updateChart: ChartDomain.UpdateChart) {
    const resource = chartCache.get(updateChart.token);

    if (resource != null) {
      const newViz = {
        visualization: {
          view_vegas: {
            ...resource.view_vegas,
            ...(updateChart.encoding ? { encoding: updateChart.encoding } : {}),
            ...(updateChart.format ? { format: updateChart.format } : {}),
            ...(updateChart.fieldFormats ? { fieldFormats: updateChart.fieldFormats } : {}),
            ...(updateChart.fieldColors ? { fieldColors: updateChart.fieldColors } : {}),
            ...(updateChart.facetSizes ? { facetSizes: updateChart.facetSizes } : {}),
            ...(updateChart.title != null ? { title: updateChart.title } : {}),
            ...(updateChart.description ? { description: updateChart.description } : {}),
            ...(updateChart.caption ? { caption: updateChart.caption } : {}),
            ...(updateChart.chartType ? { chartType: updateChart.chartType } : {}),
            ...(updateChart.isAutoUpdateDisabled != null
              ? { isAutoUpdateDisabled: updateChart.isAutoUpdateDisabled }
              : {}),
          },
          color_palette_token: updateChart.colorPaletteToken || resource.color_palette_token,
          view_version: 2, // Need to override the default value of `1` that the form has.
        },
      };

      return from(this.apiService.edit<ChartResource>(resource, newViz)).pipe(
        tap((response) => chartCache.set(updateChart.token, response)),
        map((response) => toChart(response))
      );
    } else {
      return throwError(new Error(`chart ${updateChart.token} is not in the cache`));
    }
  }

  public deleteChart(token: string) {
    const resource = chartCache.get(token);

    if (resource != null) {
      return from(this.apiService.destroy(resource));
    } else {
      return throwError(new Error(`chart ${token} is not in the cache`));
    }
  }
}
